import { AS400_BRANCH_SPLIT_LOCATIONS_BY_DIVISION } from '@erp/rpc';

// The Location filter's two inputs ⇄ the codes the query carries.
//
// RD's item-master locations are picked, as on the review page
// (LocationSelector). Every other location is typed into "Other locations":
// the locations an item master actually uses beyond those are not a list the
// app can offer. The static branch map is a different code space (stocking
// locations, `as_stock.sloc`) from the location the item and price files are
// keyed by, and enumerating the real values would mean a distinct scan of
// every ingest table. So the reader types the code they mean — one, or several
// — and the query matches records keyed by any picked or typed value.

/** How many locations one filter may carry — the contract's own cap. */
export const MAX_CATALOG_LOCATIONS = 50;

/** The longest a single value may be — a whole CDB key, the contract's cap. */
const MAX_LOCATION_LENGTH = 12;

/**
 * The codes a typed location filter means: split on commas and whitespace,
 * upper-cased (AS/400 keys are upper-case, and the match is case-insensitive
 * anyway) and de-duplicated, keeping the order they were typed in.
 *
 * Over-long entries are dropped rather than sent: the contract would reject the
 * whole request, which would read on screen as the filter having broken the page
 * instead of as one nonsense value being ignored.
 */
export function parseCatalogLocations(input: string): string[] {
  const codes = new Set<string>();
  for (const part of input.split(/[\s,]+/)) {
    const code = part.trim().toUpperCase();
    if (code === '' || code.length > MAX_LOCATION_LENGTH) continue;
    codes.add(code);
    if (codes.size === MAX_CATALOG_LOCATIONS) break;
  }
  return [...codes];
}

/**
 * The codes as the input shows them — how a restored session (or a Reset) puts
 * the filter back in the box the reader typed it into.
 */
export function formatCatalogLocations(codes: readonly string[]): string {
  return codes.join(', ');
}

/**
 * Every key the Location picker can offer: each per-branch division's code
 * followed by one of its item-master locations (`RDC`), the same keys
 * `reviewLocationDivision` builds. Static, so a restored filter can be split
 * before the org's divisions have loaded.
 */
const PICKER_LOCATION_KEYS: ReadonlySet<string> = new Set(
  Object.entries(AS400_BRANCH_SPLIT_LOCATIONS_BY_DIVISION).flatMap(
    ([division, locations]) =>
      locations.map((location) => `${division}${location}`),
  ),
);

/**
 * A saved filter split between its two inputs: the keys the picker offers
 * light it up, and everything else — a stock location, another division's key,
 * a bare letter — goes back into the "Other locations" box.
 */
export function splitCatalogLocations(codes: readonly string[]): {
  picked: string[];
  other: string[];
} {
  const picked: string[] = [];
  const other: string[] = [];
  for (const code of codes) {
    (PICKER_LOCATION_KEYS.has(code) ? picked : other).push(code);
  }
  return { picked, other };
}

/**
 * The filter both inputs make together: a record matches any picked key or any
 * typed code. De-duplicated, picked first, and capped like a typed list.
 */
export function combineCatalogLocations(
  picked: readonly string[],
  otherInput: string,
): string[] {
  return [...new Set([...picked, ...parseCatalogLocations(otherInput)])].slice(
    0,
    MAX_CATALOG_LOCATIONS,
  );
}
